import React, {useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {CustomerAddressTypes} from "../../../../../core/constants/enums";
import {Popover} from "../../popover";
import IconButton from "../../../base/button/icon_button";
import {SvgIcon} from "@mui/material";
import {ReactComponent as More} from "../../../../../assets/images/more.svg";
import classnames from "classnames";


/**
 * The actions available to the address cards of the application.
 */
export const AddressCardActions = {
    edit: 'edit',
    setDefault: 'setDefault',
    duplicate: 'duplicate',
    remove: 'remove',
    taxInfo: 'taxInfo',
};

const AddressCardActionsPopover = ({
                                       actions,
                                       edit,
                                       remove: removeProps,
                                       setDefault,
                                       duplicate,
                                       addressTypeId,
                                       disabled,
                                   }) => {
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Invokes the remove [callback] from the props.
     *
     * * sets the removing state value so that the ui can set the proper text for the remove action item.
     * @return {Promise<void>}
     */
    const remove = async () => {
        setRemoving(true);
        await removeProps();
        if (!isMounted())
            return;
        setRemoving(false);
    }

    /**
     * Closes the popover and then invokes the given action.
     * @param {function} close
     * @param {function} action
     */
    const closeDialogAndPerformAction = (close, action) => {
        close();
        action();
    }

    const popoverActions = useMemo(() => {
        const res = [];
        for (const action of actions) {
            switch (action) {
                case AddressCardActions.edit:
                    if (!edit)
                        continue;
                    res.push({
                        title: "Edit",
                        onClick: (close) => closeDialogAndPerformAction(close, edit),
                        className: "",
                    })
                    break;
                case AddressCardActions.setDefault:
                    if (!setDefault)
                        continue;
                    res.push({
                        title: "Set as Default",
                        onClick: (close) => closeDialogAndPerformAction(close, setDefault),
                        className: "",
                    })
                    break;
                case AddressCardActions.duplicate:
                    if (!duplicate)
                        continue;
                    res.push({
                        title: addressTypeId === CustomerAddressTypes.billing
                            ? "Duplicate as Shipping"
                            : "Duplicate as Billing",
                        onClick: (close) => closeDialogAndPerformAction(close, duplicate),
                        className: "",
                    })
                    break;
                case AddressCardActions.remove:
                    if (!removeProps)
                        continue;
                    res.push({
                        title: removing
                            ? "Removing..."
                            : "Remove",
                        onClick: remove,
                        className: "remove",
                    })
                    break;
                default:
                    break;
            }
        }
        return res;
    }, [actions, addressTypeId, removing])

    if (!popoverActions.length) {
        return null;
    }

    return (
        <Popover
            elevation={2}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            child={(onEnter) =>
                <IconButton
                    onPressed={onEnter}
                    disabled={disabled}
                    color={'paper.secondary'}
                >
                    <SvgIcon component={More} className='icon small'/>
                </IconButton>
            }
            popoverChild={(_, close) => (
                <div className={'address-card-popover-actions'}>
                    {
                        popoverActions?.map(e => (
                            <div
                                key={e.title}
                                className={classnames(e.className, 'action-item')}
                                onClick={() => !disabled && e.onClick(close)}
                            >
                                <p>
                                    {e.title ?? ''}
                                </p>
                            </div>
                        ))
                    }
                </div>
            )}
        />
    );
}

export default AddressCardActionsPopover;
